/**
 * "Edit in a focused modal" affordance for fenced code blocks in the source editor.
 *
 * Large embedded scripts are awkward to bulk-edit inline: selecting a whole block
 * whose tail is folded is fiddly, and eyeballing a 1000-line script (e.g. to spot
 * an unbound variable) is easier when it's alone on screen. Double-clicking
 * anywhere on the opening fence line (e.g. the ```bash line) of ANY fenced code
 * block pops the block's body into a centered modal code editor where
 * Ctrl/Cmd+A selects the whole block. (An explicit ⛶ button used to accompany
 * this on collapse-marked blocks, but the gesture alone keeps the UI cleaner.)
 *
 * This module also exports the pure geometry helpers the host uses to read and
 * rewrite a block's body range.
 */

import { EditorView } from '@codemirror/view';
import type { EditorState, Extension } from '@codemirror/state';

/** Opening code fence; group 2 is the fence run, group 3 the info string (language). */
const FENCE_OPEN_RE = /^\s*(`{3,}|~{3,})\s*([^\s`~]*)/;
/** Closing code fence: a line that is only fence characters. */
const FENCE_CLOSE_RE = /^\s*(`{3,}|~{3,})\s*$/;

export interface CodeBlock {
  /** 1-based line number of the opening fence. */
  openLineNo: number;
  /** 1-based line number of the closing fence. */
  closeLineNo: number;
  /** Document offset where the code body starts. */
  bodyFrom: number;
  /** Document offset where the code body ends. */
  bodyTo: number;
  /** False when the block is empty (open fence directly followed by close fence). */
  hasBody: boolean;
  /** Info string / language tag from the opening fence (may be ''). */
  lang: string;
}

/**
 * Scan the whole document for fenced code blocks, pairing each opener with its
 * matching closer (same fence char, at least as long). Reading geometry live from
 * the state keeps the modal correct across edits between building a button and
 * clicking it. An unterminated opener is skipped (not treated as a block).
 */
export function scanCodeBlocks(state: EditorState): CodeBlock[] {
  const { doc } = state;
  const total = doc.lines;
  const blocks: CodeBlock[] = [];

  let n = 1;
  while (n <= total) {
    const open = FENCE_OPEN_RE.exec(doc.line(n).text);
    if (!open) {
      n++;
      continue;
    }
    const fenceChar = open[1][0];
    const fenceLen = open[1].length;
    const lang = (open[2] || '').trim();

    let closeLineNo = 0;
    for (let k = n + 1; k <= total; k++) {
      const close = FENCE_CLOSE_RE.exec(doc.line(k).text);
      if (close && close[1][0] === fenceChar && close[1].length >= fenceLen) {
        closeLineNo = k;
        break;
      }
    }
    if (!closeLineNo) {
      n++;
      continue;
    }

    const hasBody = closeLineNo > n + 1;
    const bodyFrom = hasBody ? doc.line(n + 1).from : doc.line(n).to;
    const bodyTo = hasBody ? doc.line(closeLineNo - 1).to : doc.line(n).to;
    blocks.push({ openLineNo: n, closeLineNo, bodyFrom, bodyTo, hasBody, lang });
    n = closeLineNo + 1;
  }

  return blocks;
}

/** Resolve the code block whose opening fence is on the given line, or null. */
export function findCodeBlockAtFence(state: EditorState, fenceLineNo: number): CodeBlock | null {
  return scanCodeBlocks(state).find((b) => b.openLineNo === fenceLineNo) ?? null;
}

/**
 * Create the code-block-edit extension: double-clicking a block's opening
 * fence line opens the focused modal editor for that block.
 * @param onEdit Called with the opening fence's (current) line number. The host
 *   reads the block via `findCodeBlockAtFence` and opens the modal editor.
 */
export function codeBlockEditGesture(onEdit: (openLineNo: number) => void): Extension {
  return EditorView.domEventHandlers({
    dblclick(e: MouseEvent, view: EditorView): boolean {
      const pos = view.posAtCoords({ x: e.clientX, y: e.clientY });
      if (pos == null) return false;
      const lineNo = view.state.doc.lineAt(pos).number;
      const block = findCodeBlockAtFence(view.state, lineNo);
      if (!block) return false;
      // Double-click on the opening fence opens the modal instead of
      // selecting the word under the cursor.
      e.preventDefault();
      onEdit(block.openLineNo);
      return true;
    },
  });
}
